import { fetchApi } from '@/utils/fetchApi';

export interface Appointment {
  id: string;
  patient_id: string;
  patient_name: string;
  dentist_id: string;
  dentist_name: string;
  appointment_date: string;
  appointment_time: string;
  duration: number; // in minutes
  appointment_type: string;
  status: 'Scheduled' | 'Confirmed' | 'In Progress' | 'Completed' | 'Cancelled' | 'No Show';
  notes?: string;
  treatment_planned?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateAppointmentRequest {
  patient_id: string;
  dentist_id: string;
  appointment_date: string;
  appointment_time: string;
  duration: number;
  appointment_type: string;
  notes?: string;
  treatment_planned?: string;
}

export const appointmentsService = {
  async getAppointments(): Promise<Appointment[]> {
    try {
      const response = await fetchApi('appointments/api/');
      
      if (response.success && response.data) {
        return Array.isArray(response.data) ? response.data : [];
      }
      if (response.results) {
        return response.results as Appointment[];
      }
      if (Array.isArray(response)) {
        return response as Appointment[];
      }
      
      console.warn('Unexpected response format for appointments:', response);
      return [];
    } catch (error) {
      console.error('Error fetching appointments:', error);
      return [];
    }
  },

  async getAppointmentsByPatient(patientId: string): Promise<Appointment[]> {
    try {
      // Get date range for the last 2 years and next 1 year to get all appointments
      const startDate = new Date();
      startDate.setFullYear(startDate.getFullYear() - 2);
      const endDate = new Date();
      endDate.setFullYear(endDate.getFullYear() + 1);
      
      // Use the same API endpoint that the main calendar uses (Ninja API)
      const params = new URLSearchParams({
        start_date: startDate.toISOString().split('T')[0],
        end_date: endDate.toISOString().split('T')[0],
        patient_id: patientId
      });
      
      const apiUrl = `appointments/?${params.toString()}`;
      console.log('🌐 Fetching appointments from:', apiUrl);
      console.log('📅 Date range:', { start: startDate.toISOString().split('T')[0], end: endDate.toISOString().split('T')[0] });
      console.log('👤 Patient ID:', patientId);
      
      const response = await fetchApi(apiUrl);
      console.log('📨 API Response:', response);
      
      // The Ninja API returns {appointments: [...]} format
      if (response.appointments && Array.isArray(response.appointments)) {
        console.log('✅ Found appointments in response.appointments:', response.appointments);
        
        // Transform the appointments to match our interface
        const transformed = response.appointments.map((appointment: any) => ({
          id: appointment.id,
          patient_id: appointment.patient?.id || '',
          patient_name: appointment.patient?.name || '',
          dentist_id: appointment.dentist?.id || '',
          dentist_name: appointment.dentist?.name || '',
          appointment_date: appointment.start_time?.split('T')[0] || '',
          appointment_time: appointment.start_time?.split('T')[1]?.substring(0, 8) || '', // HH:MM:SS format
          duration: Math.round((new Date(appointment.end_time).getTime() - new Date(appointment.start_time).getTime()) / (1000 * 60)),
          appointment_type: appointment.treatment_type?.name || '',
          status: appointment.status || 'Scheduled',
          notes: appointment.notes || '',
          treatment_planned: appointment.treatment_type?.name || '',
          created_at: appointment.created_at || '',
          updated_at: appointment.updated_at || ''
        }));
        
        console.log('✅ Transformed appointments:', transformed);
        return transformed;
      }
      
      // Fallback for other response formats
      if (response.success && response.data) {
        console.log('✅ Found appointments in response.data:', response.data);
        return Array.isArray(response.data) ? response.data : [];
      }
      if (response.results) {
        console.log('✅ Found appointments in response.results:', response.results);
        return response.results as Appointment[];
      }
      if (Array.isArray(response)) {
        console.log('✅ Response is array, transforming calendar events:', response);
        // Transform the calendar events back to appointment format
        const transformed = response.map((event: any) => ({
          id: event.id,
          patient_id: event.extendedProps?.patientId || '',
          patient_name: event.extendedProps?.patientName || '',
          dentist_id: event.extendedProps?.dentistId || '',
          dentist_name: event.extendedProps?.dentistName || '',
          appointment_date: event.start?.split('T')[0] || '',
          appointment_time: event.start?.split('T')[1]?.split('.')[0] || '',
          duration: Math.round((new Date(event.end).getTime() - new Date(event.start).getTime()) / (1000 * 60)),
          appointment_type: event.extendedProps?.treatmentName || '',
          status: event.extendedProps?.status || 'Scheduled',
          notes: event.extendedProps?.notes || '',
          treatment_planned: event.extendedProps?.treatmentName || '',
          created_at: '',
          updated_at: ''
        }));
        console.log('🔄 Transformed appointments:', transformed);
        return transformed;
      }
      
      console.warn('⚠️ Unexpected response format for patient appointments:', response);
      return [];
    } catch (error) {
      console.error('❌ Error fetching patient appointments:', error);
      return [];
    }
  },

  async getAppointment(appointmentId: string): Promise<Appointment | null> {
    try {
      const response = await fetchApi(`appointments/api/${appointmentId}/`);
      
      if (response.success && response.data) {
        return response.data;
      }
      if (response.id) {
        return response as Appointment;
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching appointment:', error);
      return null;
    }
  },

  async createAppointment(appointmentData: CreateAppointmentRequest): Promise<Appointment> {
    try {
      const response = await fetchApi('appointments/api/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(appointmentData),
      });
      
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to create appointment');
      }
    } catch (error) {
      console.error('Error creating appointment:', error);
      throw error;
    }
  },

  async updateAppointment(appointmentId: string, appointmentData: Partial<CreateAppointmentRequest>): Promise<Appointment> {
    try {
      const response = await fetchApi(`appointments/api/${appointmentId}/`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(appointmentData),
      });
      
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to update appointment');
      }
    } catch (error) {
      console.error('Error updating appointment:', error);
      throw error;
    }
  },

  async deleteAppointment(appointmentId: string): Promise<void> {
    try {
      const response = await fetchApi(`appointments/api/${appointmentId}/`, {
        method: 'DELETE',
      });
      
      if (!response.success && response.message) {
        throw new Error(response.message);
      }
    } catch (error) {
      console.error('Error deleting appointment:', error);
      throw error;
    }
  },

  async updateAppointmentStatus(appointmentId: string, status: Appointment['status']): Promise<Appointment> {
    try {
      const response = await fetchApi(`appointments/api/${appointmentId}/status/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status }),
      });
      
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to update appointment status');
      }
    } catch (error) {
      console.error('Error updating appointment status:', error);
      throw error;
    }
  },
};